import React, { useState, useEffect } from 'react';
import Rater from 'react-rater';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';
import purify from 'dompurify';
import { Grid, Text } from 'elements';
import { RootState } from 'modules';
import PDFViewer from 'components/base/PDFViewer';
import Reply from 'components/base/reply/';
import Card from 'components/case/parts/Card';
import { showConfirm } from 'components/modal/showConfirm';
import {
  getCaseMenteeReply,
  getCaseMentorReply,
  createCaseMentorReply,
  createCaseMenteeReply,
  updateCaseMentorReply,
  updateCaseMenteeReply,
  likeCaseMentorReply,
  likeCaseMenteeReply,
  ratingCaseMentor,
  ratingCaseMentee,
  delCaseMentorReply,
  delCaseMenteeReply,
  getCaseMenteeExplainDetail,
  getCaseExplainDetail,
} from 'libs/api/case';
import { TCaseExplainDetail } from 'modules/case/types';
import { CWindowWidth } from 'constants/base.const';
import { Button } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { Colors } from 'GlobalStyles';

export type TDetailType = {
  step: string;
  mentor: string;
  mentee: string;
};

const detailTitles: TDetailType = {
  step: 'Career Step 해설',
  mentor: '멘토 해설',
  mentee: '멘티 해설',
};

type TDetail = {
  id: string;
  detail_id: string;
  type: keyof TDetailType;
  detail: TCaseExplainDetail;
  del?: (id: number) => Promise<any>;
  refresh: () => void;
};

const Detail = ({ id, detail_id, type, detail, del, refresh }: TDetail) => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const history = useHistory();

  const delExplain = () => {
    if (del) {
      showConfirm(
        async () => {
          const { success } = await del(Number(detail_id));
          if (success) history.push(`/case/board/${id}`);
        },
        '해설 삭제',
        '해설을 삭제하시겠습니까?',
      );
    }
  };

  const setRate = (ratings: number | Array<string | number>) => {
    return Array.isArray(ratings) && ratings[0] ? Number(ratings[0]) : 0;
  };

  const setRateText = (ratings: number | Array<string | number>) => {
    return Array.isArray(ratings) && ratings[1] ? `“${ratings[1]}“` : '';
  };

  const [starRate, setStarRate] = useState(0);
  const [starRateTitle, setStarRateTitle] = useState('');

  const ratingCase = async ({ rating }: { rating: number }) => {
    //리프레시를 하지 않는대신 state를 만들어서 클릭될 때마다 내가 평점한 데이터만 새로 받아옴
    let api = undefined;
    //멘티의 해설 디테일 페이지에서의 별점
    if (type === 'mentee') api = ratingCaseMentee;
    //멘토 혹은 커리어스텝 해설 디테일 페이지에서의 별점
    else api = ratingCaseMentor;
    // 별점을 누르면 post : 그 콘텐츠의 id와 별점(정수)를 함께 보냄
    const { success } = await api(detail.id, rating);
    // if (success) refresh();

    //refresh()함수를 실행하지 않고 [rate, ratetitle ]관련 정보만 다시 받아와서 setState
    if (success) {
      //여기도 멘토/커리어스텝,  멘티 나눠서
      if (type === 'step' || type === 'mentor') {
        const { success, data } = await getCaseExplainDetail(detail.id);
        if (success) {
          setStarRateTitle(setRateText(data.user_rating));
          setStarRate(setRate(data.user_rating));
        }
      } else {
        const { success, data } = await getCaseMenteeExplainDetail(detail.id);
        if (success) {
          setStarRateTitle(setRateText(data.user_rating));
          setStarRate(setRate(data.user_rating));
        }
      }
    }
  };

  //처음 렌더 될 때는 나의 이전 평점
  useEffect(() => {
    setStarRateTitle(setRateText(detail.user_rating || 0));
    setStarRate(setRate(detail.user_rating || 0));
  }, []);

  return (
    <Grid
      maxWidth={`${CWindowWidth}px`}
      padding='50px 50px'
      md={{ padding: '50px 20px' }}
      sm={{ padding: '16px' }}
    >
      <Grid
        gap='50px'
        align='center'
        justify='space-between'
        sm={{ justify: 'space-between', padding: '0 5px', isNoWrap: true }}
      >
        <Grid isInline sm={{ width: '60%' }}>
          <Text size='2rem' sm={{ size: '1.4rem' }}>
            {detailTitles[type]}
          </Text>
        </Grid>
        <Grid isInline sm={{ width: '40%' }}>
          <Button className='text' onClick={() => history.push(`/case/board/${id}`)}>
            <Grid isNoWrap align='center' gap='8px' sm={{ gap: '4px', isNoWrap: true }}>
              <LeftOutlined style={{ fontSize: 18, color: Colors.grey4 }} />
              <Grid isInline>
                <Text size='1.5rem' color={Colors.grey4} sm={{ size: '1rem' }}>
                  Case 로 돌아가기
                </Text>
              </Grid>
            </Grid>
          </Button>
        </Grid>
      </Grid>
      <Grid marginTop='35px'>
        <Grid justify='space-between' gap='3%' align='center'>
          <Grid width='47%' sm={{ width: '100%' }}>
            <Card
              type='small'
              name={detail.mentor.name || '-'}
              thumbnail={detail.mentor.profile_image}
              thumbnailTitle={detail.mentor.is_current}
              subTitle={detail.mentor.short_introduction ? `“${detail.mentor.short_introduction}“` : ''}
              rateCount={detail.rating_count}
              rate={setRate(detail.mentor_ratings || 0)}
              rateTitle={setRateText(detail.mentor_ratings || 0)}
              career={
                <Grid align='flex-end'>
                  <Grid justify='space-between'>
                    {!!detail.mentor.company && <Text>{detail.mentor.company}</Text>}
                    {!!detail.mentor.duration && <Text>{detail.mentor.duration} 근무</Text>}
                  </Grid>
                  <Grid>
                    {detail.mentor.university && detail.mentor.major && (
                      <Text>
                        {detail.mentor.university} {detail.mentor.major} 졸업
                      </Text>
                    )}
                  </Grid>
                </Grid>
              }
            />
          </Grid>
          {!!detail.key_question && detail.key_question.length > 0 && (
            <Grid width='47%' sm={{ width: '100%', padding: '20px 20px 0 20px' }}>
              <Grid>Key Questions:</Grid>
              {detail.key_question.map((q, index) => (
                <Grid key={index} gap='5px' isNoWrap>
                  <Text>∙</Text>
                  <Text>{q}</Text>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
        <Grid gap='20px' marginTop='28px'>
          <Grid gap='12px' isInline>
            <Text bold>작성일자</Text>
            <Text>{detail.created_at}</Text>
          </Grid>
          <Grid gap='12px' isInline>
            <Text bold>조회수</Text>
            <Text>{detail.hit_count}</Text>
          </Grid>
        </Grid>
        {(type === 'mentee' || (!!currentUser && currentUser.user_type !== 'mentor')) && (
          <Grid gap='20px' marginTop='28px' align='center'>
            <Text bold style={{ color: '#003159' }}>
              나의 평점
            </Text>
            <Grid isInline align='center' gap='8px'>
              <Text>“이 해설은</Text>
              {/* <Rater total={6} rating={setRate(detail.user_rating || 0)} onRate={ratingCase} /> */}
              <Rater total={6} rating={starRate} onRate={ratingCase} />
              {/* <Text bold>{setRateText(detail.mentor_ratings || 0)}</Text> */}
              <Text bold style={{ color: '#003159' }}>
                {starRateTitle.slice(1).slice(0, -1)}
              </Text>
              <Text>입니다”</Text>
            </Grid>
          </Grid>
        )}
        <Grid marginTop='28px'>
          {detail.pdf_S3_url && detail.user_phone_number && detail.user_id ? (
            // userId={detail.user_id} userPhoneNumber={detail.user_phone_number}
            <PDFViewer
              file={detail.pdf_S3_url}
              userId={detail.user_id}
              userPhoneNumber={detail.user_phone_number}
            />
          ) : (
            <Grid border={`1px solid ${Colors.grey5}`} minHeight='300px' align='flex-start'>
              <Grid padding='14px 20px' style={{ borderBottom: `1px solid ${Colors.grey5}` }}>
                <Text>{detail.title || '-'}</Text>
              </Grid>
              <Grid padding='14px 20px'>
                <div
                  dangerouslySetInnerHTML={{
                    __html: purify.sanitize(detail.content || '-'),
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        {type === 'mentee' && !!currentUser && currentUser.id === detail.mentor?.id && (
          <Grid marginTop='20px' justify='flex-end' align='center' gap='20px'>
            <Button type='primary' onClick={delExplain}>
              삭제
            </Button>
            <Button
              type='primary'
              onClick={() => history.push(`/case/board/${id}/write?explain=${detail_id}`)}
            >
              수정
            </Button>
          </Grid>
        )}
        <Grid marginTop='30px' justify='flex-end' style={{ borderTop: `1px solid ${Colors.grey1}` }} />
        {(type === 'mentee' || (!!currentUser && currentUser.user_type !== 'mentor')) && (
          <Grid gap='20px' marginTop='28px' align='center'>
            <Text bold style={{ color: '#003159' }}>
              나의 평점
            </Text>
            <Grid isInline align='center' gap='8px'>
              <Text>“이 해설은</Text>
              {/* <Rater total={6} rating={setRate(detail.user_rating || 0)} onRate={ratingCase} /> */}
              <Rater total={6} rating={starRate} onRate={ratingCase} />
              {/* <Text  bold>{setRateText(detail.mentor_ratings || 0)}</Text> */}
              <Text bold style={{ color: '#003159' }}>
                {starRateTitle.slice(1).slice(0, -1)}
              </Text>
              <Text>입니다”</Text>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid padding='25px 0' justify='center'>
        <Reply
          page_type='caseNoteDetail'
          id={Number(detail_id)}
          getReply={type === 'mentee' ? getCaseMenteeReply : getCaseMentorReply}
          onSave={type === 'mentee' ? createCaseMenteeReply : createCaseMentorReply}
          onEdit={type === 'mentee' ? updateCaseMenteeReply : updateCaseMentorReply}
          like={type === 'mentee' ? likeCaseMenteeReply : likeCaseMentorReply}
          del={type === 'mentee' ? delCaseMenteeReply : delCaseMentorReply}
        />
      </Grid>
    </Grid>
  );
};

export default Detail;
